import './GameEndScreen.css';
import { useState, useContext } from 'react';
import { AppStateContext } from '../../context/AppStateContext';

const STARS = [1, 2, 3, 4, 5];

/* dystans całej trasy - mapa nie liczy przebytej drogi, więc wartość stała */
const GAME_DISTANCE = 'ok. 2,5 km';

const GameEndScreen = ({ onClose }) => {
    const { teamData, teamToken, API_URL } = useContext(AppStateContext);

    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [liked, setLiked] = useState('');
    const [improvement, setImprovement] = useState('');
    const [otherFeedback, setOtherFeedback] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await fetch(`${API_URL}/api/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teamToken, rating, liked, improvement, otherFeedback })
            });
            setSubmitted(true);
        } catch (err) {
            console.error('Błąd wysyłania opinii:', err);
        } finally {
            setSubmitting(false);
        }
    };

    const displayRating = hoverRating || rating;

    return (
        <div className="game-end">

            <div className="game-end__content">

                {/* HERO */}
                <div className="game-end__hero">
                    <h1 className="game-end__title">Misja zakończona!</h1>
                    <p className="game-end__subtitle">
                        Gratulacje! Rozwiązaliście wszystkie zagadki i dotarliście do końca gry.
                        Wasza drużyna spisała się znakomicie – dziękujemy za udział!
                    </p>
                </div>

                {/* SCORE */}
                <div className="game-end__card game-end__score">
                    <p className="game-end__card-title">Wynik końcowy</p>
                    <div className="game-end__score-block">
                        <span className="game-end__score-team">{teamData?.name || 'Drużyna'}</span>
                        <span className="game-end__score-points">{teamData?.points ?? 0} <span className="game-end__score-label">pkt.</span></span>
                    </div>

                    <div className="game-end__stats">
                        <div className="game-end__stat">
                            <span className="game-end__stat-label">Wykorzystane podpowiedzi</span>
                            <span className="game-end__stat-value">{teamData?.hintsUsed?.length ?? 0}</span>
                        </div>
                        <div className="game-end__stat">
                            <span className="game-end__stat-label">Dystans</span>
                            <span className="game-end__stat-value">{GAME_DISTANCE}</span>
                        </div>
                    </div>
                </div>

                {/* SURVEY */}
                <div className="game-end__card game-end__survey">
                    <p className="game-end__card-title">Krótka ankieta</p>

                    {submitted ? (
                        <div className="game-end__survey-success">
                            <span className="game-end__survey-success-icon">✅</span>
                            <p className="game-end__survey-success-text">
                                Dziękujemy za opinię! Wasze uwagi pomagają nam rozwijać grę.
                            </p>
                        </div>
                    ) : (
                        <>
                            <p className="game-end__survey-intro">
                                Będziemy wdzięczni za chwilę czasu i kilka słów od Was — każda opinia
                                pomaga nam ulepszyć grę. Pod koniec miesiąca wybierzemy jedną najciekawszą opinię i nagrodzimy ją drobnym upominkiem.
                            </p>

                            {/* Rating */}
                            <div className="game-end__field">
                                <label className="game-end__label">Ogólna ocena gry</label>
                                <div className="game-end__stars">
                                    {STARS.map(star => (
                                        <button
                                            key={star}
                                            className={`game-end__star-btn${displayRating >= star ? ' -active' : ''}`}
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => setHoverRating(star)}
                                            onMouseLeave={() => setHoverRating(0)}
                                            aria-label={`Ocena ${star}`}
                                        >
                                            {displayRating >= star ? '⭐' : '☆'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Liked */}
                            <div className="game-end__field">
                                <label className="game-end__label">Co najbardziej podobało się w grze?</label>
                                <textarea
                                    className="game-end__textarea"
                                    placeholder="Np. klimat historyczny, zagadki AR, fabuła..."
                                    value={liked}
                                    onChange={e => setLiked(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            {/* Improvement */}
                            <div className="game-end__field">
                                <label className="game-end__label">Co można poprawić lub dodać?</label>
                                <textarea
                                    className="game-end__textarea"
                                    placeholder="Konstruktywna opinia jest dla nas bardzo cenna..."
                                    value={improvement}
                                    onChange={e => setImprovement(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            {/* Other */}
                            <div className="game-end__field">
                                <label className="game-end__label">Inne uwagi (opcjonalnie)</label>
                                <textarea
                                    className="game-end__textarea"
                                    placeholder="Dodatkowe przemyślenia, błędy, sugestie..."
                                    value={otherFeedback}
                                    onChange={e => setOtherFeedback(e.target.value)}
                                    rows={2}
                                />
                            </div>

                            <button
                                className="game-end__submit-btn"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Wysyłanie...' : 'Wyślij opinię'}
                            </button>
                        </>
                    )}
                </div>

                {/* PROJECT INFO */}
                <div className="game-end__card game-end__info">
                    <p className="game-end__card-title">O grze</p>
                    <p className="game-end__info-text">
                        SecretGate powstało z czystej pasji do rozwiązywania zagadek i tworzenia immersyjnych doświadczeń.
                        Jeśli masz pomysł na nową zagadkę albo chcesz podzielić się opinią,
                        napisz do nas na{' '}
                        <a href="mailto:kontakt@secretgate.pl">kontakt@secretgate.pl</a>.
                        Dziękujemy, że byliście z nami! 🙌
                    </p>
                </div>

            </div>
        </div>
    );
};

export default GameEndScreen;
